import { General } from "../../siteManagement/general.js";
import { HTTPService } from "../core/util/httpManager.js";
import { Stock, } from "./stocksGrid.js";
import { Grid } from "../core/components/grid.js";
import { Helper } from "../core/util/helper.js";
import { Config } from "../core/config/config.js";
import { Sheet } from "../../siteManagement/sheet.js";
import { StocksContext } from "./stocks.js";
import { StocksService } from "./stocksService.js";
import { KeyValuePair } from "../commonInterfaces.js";
import React from "react";

export const OpenHistoryGrid = async (stock: Stock, context: StocksContext, title: string) => {
    let [data] = await Promise.all([
        HTTPService.post("Stock/GetHistory", {
            ArtNr: stock.ArtNr,
            ChargenNr: stock.ChargenNr,
            SerienNr: stock.SerienNr,
            LagerplatzId: stock.StorageSpaceId
        })
    ]);
    const sheet = General.Sheet.Manager.createSheet();
    const props: HistoryGridProps = {
        data: data,
        stock: stock,
        context: context,
    }
    sheet.show(() => {
        sheet.mountContent(HistoryGrid, props);
        sheet.setTitle(title);
    });
}

interface GridItem {
    Id: number;
    ProcessType: number;
    Amount: number;
    CreatedOn: string | Date;
    CreatedBy: string;
    AdditionalData: string;
}

interface HistoryGridProps { data: GridItem[], stock: Stock, context: StocksContext}

class HistoryGrid extends React.Component<HistoryGridProps, { selected: GridItem }>{
    private gridOptions: kendo.ui.GridOptions;
    private INGOING_GOODS = 1;
    private TRANSFER_IN = 2;
    private TRANSFER_OUT = 3;
    private OUTGOING_GOODS = 4;

    private incomingGoodsTypes: KeyValuePair<number, string>[] = [];
    private outgoingGoodsTypes: KeyValuePair<number, string>[] = [];
    private transferTypes: KeyValuePair<number, string>[] = [];

    constructor(props) {
        super(props)
        this.state = {
            selected: null
        }
        this.initApp();
    }

    private initApp = async () => {
        this.gridOptions = {
            sortable: true,
            pageable: false,
            groupable: false,
            selectable: true,
            resizable: true,
            scrollable: true,
            filterable: {
                extra: false
            },
            editable: false,
            columns: [
                {
                    field: "ProcessType",
                    title: Helper.getTranslation("Field.BelegKontierung.stdart"),
                    template: (dataItem: GridItem) => {
                        switch (dataItem.ProcessType) {
                            case this.INGOING_GOODS:
                                return Helper.getTranslation("WebSMS.Warehouse.IncomingGoods")
                            case this.TRANSFER_IN:
                                return "Verschiebung ins Lager"
                            case this.TRANSFER_OUT:
                                return "Verschiebung aus dem Lager"
                            case this.OUTGOING_GOODS:
                                return Helper.getTranslation("WebSMS.Warehouse.OutgoingGoods")
                            default:
                                    return "n/a"
                        }
                    }
                },
                {
                    field: "Amount",
                    title: Helper.getTranslation("Parts.ChargenSerien.Menge"),
                    template: (dataItem: GridItem) => {
                        switch (dataItem.ProcessType) {
                            case this.INGOING_GOODS:
                            case this.TRANSFER_IN:
                                return dataItem.Amount;
                            case this.TRANSFER_OUT:
                            case this.OUTGOING_GOODS:
                                return -1 * dataItem.Amount
                            default:
                        }
                    }
                },
                {
                    field: "CreatedBy",
                    title: Helper.getTranslation("WebSMS.Ticket.CreatedBy"),
                },
                {
                    field: "CreatedOn",
                    title: Helper.getTranslation("WebSMS.Ticket.CreatedOn"),
                    template: (dataItem: GridItem) => {
                        return kendo.toString(kendo.parseDate(dataItem.CreatedOn.toString()), 'g')
                    }
                }
            ]
        }
        this.incomingGoodsTypes = await StocksService.getTypes(1);
        this.outgoingGoodsTypes = await StocksService.getTypes(2);
        this.transferTypes = await StocksService.getTypes(3);
        this.setState({})
    }

    private renderIncomingGoods = () => {
        const { ADRNR, LIEFERSCHEINNR, VTYPID, ZIELADRNR } = JSON.parse(this.state.selected.AdditionalData)[0]
        return (
            <div style={{ display: "flex", flexDirection: "column", width: "320px", margin: "5px" }}>
                <i style={{ fontSize: "4em", margin: "3px" }} className="fa-light fa-truck"></i>
                {VTYPID ? this.renderField("Field.Ansprechpartner.typ", this.incomingGoodsTypes.find(t => t.Key == VTYPID)?.Value) : null}
                {LIEFERSCHEINNR ? this.renderField("WebSMS.Warehouse.Lieferschein", LIEFERSCHEINNR) : null}
                {ADRNR ? this.renderField("Field.Adresse.adrnr", ADRNR) : null}
                {ZIELADRNR ? this.renderField("WebSMS.Warehouse.IncomingGoods.TargetAddress", ZIELADRNR) : null }
            </div>
        );
    }

    private renderTransferIn = () => {
        const { SOURCEID, VTYPID} = JSON.parse(this.state.selected.AdditionalData)[0]
        return (
            <div style={{ display: "flex", flexDirection: "column", width: "320px", margin: "5px" }}>
                <i style={{ fontSize: "4em", margin: "3px" }} className="fa-light fa-arrow-right"></i>
                {VTYPID ? this.renderField("Field.Ansprechpartner.typ", this.transferTypes.find(t => t.Key == VTYPID)?.Value) : null}
                {SOURCEID ? this.renderField("WebSMS.Warehouse.StorageSpace", this.props.context.storaceSpaces[SOURCEID]) : null}
            </div>
        );
    }

    private renderTransferOut = () => {
        const { SOURCEID, VTYPID } = JSON.parse(this.state.selected.AdditionalData)[0]
        return (
            <div style={{ display: "flex", flexDirection: "column", width: "320px", margin: "5px" }}>
                <i style={{ fontSize: "4em", margin: "3px" }} className="fa-light fa-arrow-left"></i>
                {VTYPID ? this.renderField("Field.Ansprechpartner.typ", this.transferTypes.find(t => t.Key == VTYPID)?.Value) : null}
                {SOURCEID ? this.renderField("WebSMS.Warehouse.StorageSpace", this.props.context.storaceSpaces[SOURCEID]) : null}
            </div>
        );
    }

    private renderOutgoingGoods = () => {
        const { BELEGNR, VTYPID } = JSON.parse(this.state.selected.AdditionalData)[0]
        return (
            <div style={{ display: "flex", flexDirection: "column", width: "320px", margin: "5px" }}>
                <i style={{ fontSize: "4em", margin: "3px", transform: "scaleX(-1)", width: "fit-content" }} className="fa-light fa-truck"></i>
                {VTYPID ? this.renderField("Field.Ansprechpartner.typ", this.outgoingGoodsTypes.find(t => t.Key == VTYPID)?.Value) : null}
                {BELEGNR ? this.renderField("Field.Beleg.belegnr", BELEGNR) : null}
            </div>
        );
    }

    private renderField = (translationKey: string, value: string) => {
        return (
            <div style={{ margin: "3px", display: "flex" }}>
                <div style={{ width: "160px" }} className="truncate">{Helper.getTranslation(translationKey)}:</div>
                <div>{value}</div>
            </div>
        )
    }

    render() {
        const onSelectionChanged = (dataItem) => {
            this.setState({ selected: dataItem })
        }
        let info: JSX.Element = null;
        if (this.state.selected) {
            switch (this.state.selected.ProcessType) {

                case this.INGOING_GOODS:
                    info = this.renderIncomingGoods();
                    break;
                case this.TRANSFER_IN:
                    info = this.renderTransferIn();
                    break;
                case this.TRANSFER_OUT:
                    info = this.renderTransferOut();
                    break;
                case this.OUTGOING_GOODS:
                    info = this.renderOutgoingGoods()
                    break;
            }
        }
        return (
            <div style={{ display: "flex", height: "100%" }}>
                <div style={{ flex: 1 }} className="flex-container">
                    <Grid
                        data={this.props.data}
                        options={this.gridOptions}
                        id={`stocks_history`}
                        onSelectionChanged={onSelectionChanged}
                    />
                </div>
                {info}
            </div>
            
        )
    }
}